import type { FamilyDescriptor, FamilyUnavailable } from '@beyond-js/ui/react';
import type { Api } from '@cdn/api';
import { Observable } from '@cdn/state';

/** What the family bar renders: the descriptor, `null` while it loads, or `{unavailable: true}` */
export type Descriptor = FamilyDescriptor | FamilyUnavailable | null;

/**
 * The family bar's descriptor (`beyond-family/1`) for the place in view, read through the service's relay
 * (`GET /v1/family`), which asks Beyond Projects with CDN's consumer credential and the person's own session.
 *
 * The place is the organization of the address and, when CDN knows it, the common project in view: the
 * project a list was opened for (`?project=`) or the project an application belongs to. Setting either reads
 * the descriptor again once per change, however many screens set it in one render; an answer that arrives
 * after a newer place was set is dropped. The previous descriptor stays while the next one loads, so the bar
 * never jumps.
 *
 * The bar must never break a page: no answer, a refusal other than an ended session, or a document of another
 * protocol all become `{unavailable: true}`, and the bar shows the names this application knows itself.
 * When the service says it does not use Projects (`not_configured`), nothing is asked again while the page
 * lives.
 */
export class Family extends Observable {
	static PROTOCOL = 'beyond-family/1';

	#api: Api;
	#descriptor: Descriptor = null;
	#organization?: string;
	#project?: string;
	#asked?: string;
	#turn = 0;
	#scheduled = false;
	#standalone = false;

	constructor(api: Api) {
		super();
		this.#api = api;
	}

	get descriptor(): Descriptor {
		return this.#descriptor;
	}

	/** Whether the service said this installation runs without Beyond Projects (`not_configured`) */
	get standalone() {
		return this.#standalone;
	}

	/** Whether Projects was asked and did not answer: the descriptor is unavailable although CDN uses Projects */
	get unanswered() {
		return !this.#standalone && this.#descriptor !== null && this.#descriptor.unavailable === true;
	}

	get organization() {
		return this.#organization;
	}

	set organization(value: string | undefined) {
		this.#organization = value || undefined;
		this.#schedule();
	}

	get project() {
		return this.#project;
	}

	set project(value: string | undefined) {
		this.#project = value || undefined;
		this.#schedule();
	}

	/**
	 * Asks Projects again for the place in view, after it did not answer
	 *
	 * @returns Whether it answered: false while Projects, or the relay, still does not
	 */
	async retry(): Promise<boolean> {
		if (this.#standalone) return true;
		this.#asked = undefined;
		await this.#load();
		return !this.unanswered;
	}

	// Several setters in one render read once, after they all ran
	#schedule() {
		if (this.#scheduled) return;
		this.#scheduled = true;
		queueMicrotask(() => {
			this.#scheduled = false;
			void this.#load();
		});
	}

	async #load() {
		const search = new URLSearchParams();
		this.#organization && search.set('organization', this.#organization);
		this.#project && search.set('project', this.#project);
		const query = search.toString();
		if (this.#standalone || query === this.#asked) return;
		this.#asked = query;

		const turn = ++this.#turn;
		let answer: unknown;
		try {
			answer = await this.#api.relay('GET', `/v1/family${query ? `?${query}` : ''}`);
		} catch {
			answer = undefined;
		}
		if (turn !== this.#turn) return;

		const document = answer as { protocol?: unknown; unavailable?: unknown; reason?: unknown } | undefined;
		if (document?.unavailable === true && document.reason === 'not_configured') this.#standalone = true;
		this.#descriptor = document?.protocol === Family.PROTOCOL ? (document as FamilyDescriptor) : { unavailable: true };
		this.changed();
	}
}
